import { IconSystemV2CourseBookCourse } from '@doltech/icons/SystemV2/IconSystemV2CourseBookCourse';
import { IconSystemV2CourseTestDoTestListening } from '@doltech/icons/SystemV2/IconSystemV2CourseTestDoTestListening';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { PageTypeEnum, SkillEnum, TestStatusEnum } from '@doltech/utils/lib/constants';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import cl from 'classnames';
import * as React from 'react';
import dynamic from 'next/dynamic';

import { IconSystemV2CourseHistory } from '@doltech/icons/SystemV2/IconSystemV2CourseHistory';
import { IconSystemV2CourseTestVocabulary } from '@doltech/icons/SystemV2/IconSystemV2CourseTestVocabulary';
import { IconSystemV2InterfaceEssentialAnswerCheck2 } from '@doltech/icons/SystemV2/IconSystemV2InterfaceEssentialAnswerCheck2';
import { IconSystemV2SpecificBulb2 } from '@doltech/icons/SystemV2/IconSystemV2SpecificBulb2';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { ItemDetailDoTestButton } from '@doltech/ui/lib/shared/components/ItemDetailDoTestButton/ItemDetailDoTestButton';

import { routingUrls } from '@doltech/core/lib/routing/routing.config';
import styled from 'styled-components';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';
import { nanoid } from 'nanoid';

const Progress = dynamic<any>(() => import('antd').then((mod) => mod.Progress), {
  ssr: false,
});

const ActionsType = [
  PageTypeEnum.VIEW_VOCAB.toString(),
  PageTypeEnum.VIEW_SOLUTION.toString(),
  PageTypeEnum.VIEW_TEST_QUESTION.toString(),
];

interface CardOnlineTestProps {
  test: any;
  skill: string;
}

export const CardOnlineTest = (props: CardOnlineTestProps) => {
  const { test, skill } = props;
  const { overview } = test || {};
  const status = overview?.status || TestStatusEnum.NOT_STARTED;
  const { isLoggedIn } = useGetAuthInfo();
  const disabled = !test;
  const doTest = test?.pages.find(
    ({ templateTypeId, url }) => url && templateTypeId === PageTypeEnum.DO_TEST
  );

  const actionsUrl = React.useMemo(() => {
    return (
      test?.pages
        .filter(({ templateTypeId, url }) => url && ActionsType.includes(templateTypeId))
        .sort(
          (a, b) => ActionsType.indexOf(a?.templateTypeId) - ActionsType.indexOf(b?.templateTypeId)
        )
        .map((page) => {
          const { templateTypeId, url } = page;
          let icon;
          let text;
          switch (templateTypeId) {
            case PageTypeEnum.VIEW_TEST_QUESTION: {
              icon = <IconSystemV2InterfaceEssentialAnswerCheck2 color="black60" size={20} />;
              text = 'Đề và đáp án';
              break;
            }
            case PageTypeEnum.VIEW_SOLUTION: {
              icon = <IconSystemV2SpecificBulb2 color="black60" size={20} />;
              text = 'Giải thích';
              break;
            }
            case PageTypeEnum.VIEW_VOCAB:
            default: {
              icon = <IconSystemV2CourseTestVocabulary color="black60" size={20} />;
              text = 'Từ vựng';
              break;
            }
          }

          return {
            key: templateTypeId,
            url: getLandingUrlFromPath(url),
            icon,
            text,
          };
        }) || []
    );
  }, [test]);

  const { color, percent } = React.useMemo(() => {
    switch (status) {
      case TestStatusEnum.NOT_STARTED:
        return {
          color: 'black80',
          percent: 0,
        };

      case TestStatusEnum.IN_PROGRESS:
        return {
          color: 'blue100',
          percent: Math.round((overview?.totalAnsweredQuestions / overview?.totalQuestions) * 100),
        };
      case TestStatusEnum.COMPLETED:
        return {
          color: 'green100',
          percent: 100,
        };
    }
  }, [status, overview?.totalQuestions, overview?.totalAnsweredQuestions]);

  const renderCircleContent = React.useCallback(() => {
    switch (status) {
      case TestStatusEnum.NOT_STARTED:
      case TestStatusEnum.IN_PROGRESS:
        return skill === SkillEnum.LISTENING ? (
          <IconSystemV2CourseTestDoTestListening fontSize={28} color={color} />
        ) : (
          <IconSystemV2CourseBookCourse fontSize={28} color={color} />
        );
      case TestStatusEnum.COMPLETED:
        return (
          <>
            {skill === SkillEnum.LISTENING ? (
              <IconSystemV2CourseTestDoTestListening fontSize={16} color={color} />
            ) : (
              <IconSystemV2CourseBookCourse fontSize={16} color={color} />
            )}
            <ResponsiveTypography.Paragraph color="green100" variant="bold/20-28">
              {test?.overview?.test?.score ? test?.overview?.test?.score.toFixed(1) : '0.0'}
            </ResponsiveTypography.Paragraph>
          </>
        );
    }
  }, [status, color, test?.overview?.test?.score, skill]);

  return (
    <TestItem key={test?.id} className={cl('test-item', { disabled })}>
      <Progress
        width={60}
        type="circle"
        strokeWidth={5.71}
        strokeColor={colorsV2[color]}
        trailColor={colorsV2.gray20}
        percent={percent}
        format={() => renderCircleContent()}
      />
      <ItemDetailDoTestButton
        item={{
          type: 'TEST',
          status: test?.overview?.status,
          url: isLoggedIn ? doTest?.url : `${doTest?.url}?id=${nanoid()}_trial`,
        }}
        altActions={[
          ...actionsUrl,
          test?.hasHistory && {
            key: 'view_history',
            url: routingUrls.landing.STUDENT_TEST_HISTORY_FULL_PATH(test?.id),
            icon: <IconSystemV2CourseHistory color="black60" />,
            text: 'Lịch sử làm bài',
          },
        ].filter(Boolean)}
      />
    </TestItem>
  );
};

const TestItem = styled.div`
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  justify-items: center;
  gap: 16px;
  &.disabled {
    pointer-events: none;

    svg,
    span {
      color: ${colorsV2.gray20};
    }
  }
`;
